import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { ArrowRight, BookOpen, Download, Loader2, Lock, Unlock } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { getStripe, getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe";
import { trackBuiltIndexStep } from "@/lib/built-index-analytics";
import { syncLeadToCrm } from "@/lib/crm-sync";

import {
  BUILT_BOOK_URL,
  BUILT_INDEX_BOOKING_HREF,
  BUILT_INDEX_ROUTES,
  BUILT_PRINCIPLES,
  DISC_PROFILES,
  FULL_REPORT_PRICE_USD,
  LOVE_LANGUAGES,
  LOVE_PROFILES,
  LOVIN_DIMENSIONS,
} from "@/content/built-index";
import {
  createBuiltIndexCheckout,
  getBuiltIndexRecord,
  readSession,
  unlockBuiltIndex,
  type BuiltIndexRecord,
} from "@/lib/built-index-session";

const Card = ({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) => (
  <div className="border border-white/10 bg-white/[0.04] p-5">
    <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{label}</p>
    <p className="font-display uppercase text-white text-2xl mt-2 leading-tight">{value}</p>
    {sub && <p className="text-silver text-sm mt-2 leading-relaxed">{sub}</p>}
  </div>
);

const BuiltIndexResults = () => {
  const [params] = useSearchParams();
  const localId = readSession().id;
  const id = params.get("id") ?? localId ?? "";
  const stripeSessionId = params.get("session_id");

  const [record, setRecord] = useState<BuiltIndexRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!id) {
        setError("We could not find your assessment. Start again to get your score.");
        setLoading(false);
        return;
      }
      try {
        // Returning from Stripe: confirm the payment before reading the record.
        if (stripeSessionId) {
          try {
            await unlockBuiltIndex({
              id,
              sessionId: stripeSessionId,
              environment: getStripeEnvironment(),
            });
          } catch {
            /* fall through — the record read below shows the locked state */
          }
        }
        const data = await getBuiltIndexRecord(id);
        if (active) {
          setRecord(data);
          trackBuiltIndexStep("results_view", {
            sessionId: id,
            metadata: { paid: data.paid, built_total: data.builtTotal ?? undefined },
          });
          const email = readSession().email;
          if (email) {
            syncLeadToCrm({
              event: data.paid ? "built_index_unlock" : "built_index_complete",
              email,
              fullName: data.fullName,
              role: data.role,
              builtTotal: data.builtTotal,
              builtTier: data.builtTier,
              discType: data.discType,
              loveFuel: data.loveFuel,
              loveDrain: data.loveDrain,
              lovinScore: data.lovinScore,
              lovinTier: data.lovinTier,
              paid: data.paid,
              source: "built_index_results",
            });
          }
          if (data.paid) {
            trackBuiltIndexStep("unlock", {
              sessionId: id,
              metadata: { via: stripeSessionId ? "checkout_return" : "existing" },
            });
          }
        }

      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : "Something went wrong.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [id, stripeSessionId]);

  const fetchClientSecret = useCallback(async () => {
    const { clientSecret } = await createBuiltIndexCheckout({
      id,
      returnUrl: `${window.location.origin}${BUILT_INDEX_ROUTES.results}?id=${id}&session_id={CHECKOUT_SESSION_ID}`,
      environment: getStripeEnvironment(),
    });
    return clientSecret;
  }, [id]);

  const checkoutOptions = useMemo(() => ({ fetchClientSecret }), [fetchClientSecret]);

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPdf = useCallback(async () => {
    if (!record) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const { downloadBuiltIndexPdf } = await import("@/lib/built-index-pdf");
      await downloadBuiltIndexPdf(record);
      trackBuiltIndexStep("report_download", {
        sessionId: record.id,
        metadata: { built_tier: record.builtTier ?? undefined },
        once: false,
      });
    } catch {
      setPdfError("We couldn't build your PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  }, [record]);


  const languageName = (key: string | null | undefined) =>
    LOVE_LANGUAGES.find((l) => l.key === key)?.name ?? "—";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Your Built Index™ Results · Dr. Sully Speaks"
        description="Your leadership diagnostic results across the MAKECHANGE principles, DISC style, fuel & drain, and listening."
        path="/built-index/results"
        noindex
      />
      <PaymentTestModeBanner />
      <SiteNav />

      <section className="relative pt-32 pb-20 md:pt-36 bg-hero">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,hsl(var(--gold)/0.1),transparent_55%)]"
        />
        <div className="container relative max-w-4xl">
          {loading && (
            <p className="flex items-center gap-2 text-silver">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your results…
            </p>
          )}

          {!loading && error && (
            <div className="border border-white/10 bg-white/[0.04] p-8">
              <p className="text-white">{error}</p>
              <Link
                to={BUILT_INDEX_ROUTES.start}
                className="mt-5 inline-flex items-center gap-2 bg-orange text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold"
              >
                Start the assessment <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {!loading && record && (
            <>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                The Built Index™ · Results
              </p>
              <h1 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-3 leading-[1.05]">
                {record.fullName?.split(" ")[0] ?? "Your"} — you scored{" "}
                <span className="text-gradient-gold">{record.builtTotal ?? 0}/100</span>
              </h1>
              <p className="text-silver mt-4 leading-relaxed max-w-2xl">
                Tier: <strong className="text-white">{record.builtTier}</strong>. This is a
                measurement, not a verdict. What matters is what you rebuild next.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <Card
                  label="Leadership Style"
                  value={record.discType ? DISC_PROFILES[record.discType].name : "—"}
                  sub={record.discType ? DISC_PROFILES[record.discType].tagline : undefined}
                />
                <Card
                  label="Listening (LOVIN)"
                  value={`${record.lovinScore ?? 0}/50`}
                  sub={record.lovinTier ?? undefined}
                />
                <Card label="Primary Fuel" value={languageName(record.loveFuel)} />
                <Card label="Primary Drain" value={languageName(record.loveDrain)} />
              </div>

              {record.paid ? (
                <div className="mt-12 space-y-10">
                  <div className="border border-orange/40 bg-orange/10 p-6 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-orange">
                        Take it with you
                      </p>
                      <p className="text-white mt-2 text-sm leading-relaxed max-w-md">
                        Download your full Built Letter and score breakdown as a PDF.
                      </p>
                      {pdfError && (
                        <p className="text-orange text-xs mt-2" role="alert">
                          {pdfError}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadPdf}
                      disabled={pdfBusy}
                      className="inline-flex items-center gap-2 bg-orange text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition disabled:opacity-60"
                    >
                      {pdfBusy ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Preparing…
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" /> Download PDF report
                        </>
                      )}
                    </button>
                  </div>

                  <div className="border border-gold/30 bg-white/[0.04] p-6 md:p-8">

                    <p className="text-[10px] uppercase tracking-[0.25em] text-gold flex items-center gap-2">
                      <Unlock className="h-3.5 w-3.5" /> Your Built Letter
                    </p>
                    <div className="mt-4 space-y-4 text-silver leading-relaxed whitespace-pre-line">
                      {record.builtLetter}
                    </div>
                  </div>

                  <div>
                    <h2 className="font-display uppercase text-white text-2xl">
                      Principle breakdown
                    </h2>
                    <ul className="mt-4 space-y-2">
                      {BUILT_PRINCIPLES.map((p) => {
                        const score = record.builtScores?.[p.key] ?? 0;
                        return (
                          <li key={p.key} className="flex items-center gap-4">
                            <span className="w-40 shrink-0 text-sm text-white">
                              {p.letter} · {p.name}
                            </span>
                            <span className="h-2 flex-1 bg-white/10">
                              <span
                                className="block h-2 bg-orange"
                                style={{ width: `${score * 10}%` }}
                              />
                            </span>
                            <span className="w-10 text-right text-sm text-silver">
                              {score}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {record.discType && (
                    <div>
                      <h2 className="font-display uppercase text-white text-2xl">
                        Your style: {DISC_PROFILES[record.discType].name}
                      </h2>
                      <p className="text-silver mt-3 leading-relaxed">
                        {DISC_PROFILES[record.discType].summary}
                      </p>
                      <p className="text-white mt-3 leading-relaxed">
                        <strong>Built move: </strong>
                        {DISC_PROFILES[record.discType].builtMove}
                      </p>
                    </div>
                  )}

                  {record.loveFuel && (
                    <div>
                      <h2 className="font-display uppercase text-white text-2xl">
                        Fuel &amp; drain booklet
                      </h2>
                      <p className="text-silver mt-3 leading-relaxed">
                        {LOVE_PROFILES[record.loveFuel].booklet}
                      </p>
                    </div>
                  )}

                  <div>
                    <h2 className="font-display uppercase text-white text-2xl">
                      Listening breakdown
                    </h2>
                    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                      {LOVIN_DIMENSIONS.map((d) => (
                        <li
                          key={d.key}
                          className="flex items-center justify-between border border-white/10 px-4 py-3 text-sm"
                        >
                          <span className="text-white">
                            {d.letter} · {d.name}
                          </span>
                          <span className="text-gold">
                            {record.lovinAnswers?.[d.key] ?? 0}/10
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border border-orange/40 bg-orange/10 p-6 md:p-8">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-orange flex items-center gap-2">
                      <BookOpen className="h-3.5 w-3.5" /> Included with your report
                    </p>
                    <h2 className="font-display uppercase text-white text-2xl mt-3">
                      Your free copy of Built On Purpose
                    </h2>
                    <div className="mt-5 flex flex-wrap gap-3">
                      <a
                        href={BUILT_BOOK_URL}
                        onClick={() =>
                          trackBuiltIndexStep("book_delivery", {
                            sessionId: record.id,
                            metadata: { destination: BUILT_BOOK_URL },
                            once: false,
                          })
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-orange text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition"
                      >
                        Read the book <ArrowRight className="h-4 w-4" />
                      </a>
                      <Link
                        to={BUILT_INDEX_BOOKING_HREF}
                        className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:border-gold/60 transition"
                      >
                        Book 1:1 with Dr. Sully
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-12 border border-gold/30 bg-white/[0.04] p-6 md:p-8">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5" /> Locked
                  </p>
                  <h2 className="font-display uppercase text-white text-2xl md:text-3xl mt-3">
                    Unlock the full report — ${FULL_REPORT_PRICE_USD}
                  </h2>
                  <ul className="mt-5 space-y-2 text-silver text-sm">
                    <li>· All ten principle scores with your leakage points</li>
                    <li>· Your DISC profile and the built move for your style</li>
                    <li>· Your personalized fuel &amp; drain booklet</li>
                    <li>· Your Built Letter, written in Dr. Sully&rsquo;s voice</li>
                    <li>· A free digital copy of Built On Purpose</li>
                  </ul>

                  {!isPaymentsConfigured() ? (
                    <p className="mt-6 text-silver text-sm">
                      Checkout is not available on this build yet. Please try again shortly.
                    </p>
                  ) : checkoutOpen ? (
                    <div className="mt-6 bg-white p-2">
                      <EmbeddedCheckoutProvider stripe={getStripe()} options={checkoutOptions}>
                        <EmbeddedCheckout />
                      </EmbeddedCheckoutProvider>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        trackBuiltIndexStep("checkout_start", { sessionId: id });
                        setCheckoutOpen(true);
                      }}
                      className="mt-6 inline-flex items-center gap-2 bg-orange text-primary-foreground px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition"
                    >
                      Unlock My Full Report <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default BuiltIndexResults;
